import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { ReactSession } from "react-client-session";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import config from "./global/config";
import React from "react";
import {
  Modal,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
} from "@mui/material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  width: { xs: "90%", sm: "80%", md: "60%" },
  maxHeight: "90vh",
  overflowY: "auto",
};

// Add Roles Modal
export const AddRolesModal = ({
  open,
  handleClose,
  handleSubmit,
  rolesData,
  handleChange,
  categories = [],
}) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ mb: 3, color: "primary.main" }}
        >
          Ajouter un nouveau roles
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role"
                name="role"
                value={rolesData.role || ""}
                onChange={handleChange}
                multiline
                rows={2}
                required
              />
            </Grid>
          </Grid>

          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button variant="outlined" onClick={handleClose}>
              Annuler
            </Button>
            <Button variant="contained" type="submit" color="secondary">
              Enregistrer
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

// Edit Roles Modal with the same fixes
export const EditRolesModal = ({
  open,
  handleClose,
  handleSubmit,
  rolesData,
  handleChange,
}) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ mb: 3, color: "primary.main" }}
        >
          Modifier le role
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role"
                name="role"
                value={rolesData.role || ""}
                onChange={handleChange}
                multiline
                rows={2}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Role"
                name="new_role"
                value={rolesData.new_role || ""}
                onChange={handleChange}
                multiline
                rows={2}
                required
              />
            </Grid>
          </Grid>

          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button variant="outlined" onClick={handleClose}>
              Annuler
            </Button>
            <Button variant="contained" type="submit" color="secondary">
              Mettre à jour
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

// Delete Roles Modal remains the same
export const DeleteRolesModal = ({
  open,
  handleClose,
  handleDelete,
  roles = {},
}) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ mb: 3, color: "error.main" }}
        >
          Confirmer la suppression
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Êtes-vous sûr de vouloir supprimer le role suivant ?
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Role:</strong> {roles.role || ""}
        </Typography>

        <Box
          sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Annuler
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Supprimer
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

function Roles() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [data, setData] = useState([]);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [dataCategorie, setDataCategorie] = useState([]);

  const [rolesSelected, setRolesSelected] = useState({
    role: "",
  });

  const api = axios.create({
    baseURL: config.apiUrl,
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const clearSelected = () => {
    setRolesSelected({
      role: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRolesSelected((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const openCloseModalInsert = () => {
    setModalAdd(!modalAdd);
    if (!modalAdd) clearSelected();
  };

  const openCloseModalEdit = () => {
    setModalEdit(!modalEdit);
  };

  const openCloseModalDelete = () => {
    setModalDelete(!modalDelete);
  };

  const requestGet = async () => {
    try {
      const response = await api.get("roles.php");
      setData(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      console.log(error);
    }
  };

  const requestPost = async () => {
    try {
      const formData = new FormData();
      formData.append("role", rolesSelected.role);
      formData.append("METHOD", "POST");

      const response = await api.post("roles.php", formData);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      if (response.data.role) {
        // Update the data state directly
        setData([...data, { role: response.data.role }]);
        openCloseModalInsert();
        requestGet();
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }

      alert(error.message || "Error adding role. Please try again.");
      console.error(error);
    }
  };

  const requestPut = async () => {
    try {
      const deleteFormData = new FormData();
      deleteFormData.append("METHOD", "DELETE");
      await api.post(
        `roles.php?role=${encodeURIComponent(rolesSelected.role)}`,
        deleteFormData
      );

      const insertFormData = new FormData();
      insertFormData.append("role", rolesSelected.new_role);
      insertFormData.append("user_upd", ReactSession.get("username"));
      insertFormData.append("METHOD", "POST");
      const response = await api.post("roles.php", insertFormData);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      requestGet();
      openCloseModalEdit();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      console.error(error);
      alert(error.message || "Error updating role. Please try again.");
    }
  };

  const requestDelete = async () => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "DELETE");

      await api.post(`roles.php?role=${rolesSelected.role}`, formData);
      setData(data.filter((roles) => roles.role !== rolesSelected.role));
      openCloseModalDelete();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      console.log(error);
    }
  };

  const selectRoles = (roles, action) => {
    setRolesSelected(roles);
    console.log("Selected roles:", roles);

    if (action === "Edit") {
      openCloseModalEdit();
    } else {
      openCloseModalDelete();
    }
  };

  const columns = [
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => selectRoles(params.row, "Edit")}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => selectRoles(params.row, "Delete")}
          color="inherit"
        />,
      ],
    },
  ];

  useEffect(() => {
    requestGet();
  }, []);

  return (
    <Box m="25px">
      <NavigationHeader title="Roles" subtitle="Liste des Roles" />

      <Box display="flex" justifyContent="end" mt="20px">
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          onClick={openCloseModalInsert}
        >
          Nouveau Roles
        </Button>
        <AddRolesModal
          open={modalAdd}
          handleClose={openCloseModalInsert}
          handleSubmit={(e) => {
            e.preventDefault();
            requestPost();
          }}
          rolesData={rolesSelected}
          handleChange={handleChange}
          categories={dataCategorie}
        />

        <EditRolesModal
          open={modalEdit}
          handleClose={openCloseModalEdit}
          handleSubmit={(e) => {
            e.preventDefault();
            requestPut();
          }}
          rolesData={rolesSelected}
          handleChange={handleChange}
          categories={dataCategorie}
        />

        <DeleteRolesModal
          open={modalDelete}
          handleClose={openCloseModalDelete}
          handleDelete={requestDelete}
          roles={rolesSelected}
        />
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.role}
          columns={columns}
          slots={{
              toolbar: CustomToolbar,
            }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </Box>
  );
}

// CustomToolbar and other components remain the same...

// CSV Export Options
const csvOptions = {
  fileName: "RolesDatabase",
  delimiter: ";",
  utf8WithBom: true,
};

// Print Export Options
const printOptions = {
  hideFooter: true,
  hideToolbar: true,
  includeCheckboxes: true,
};

// Custom Export Buttons Component
function CustomExportButtons(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem options={csvOptions} />
      <GridPrintExportMenuItem options={printOptions} />
    </GridToolbarExportContainer>
  );
}

// Custom Toolbar Component
function CustomToolbar(props) {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <CustomExportButtons />
      <Box
        sx={{
          marginLeft: "auto",
          pl: 0,
          pr: 0,
          pb: 0,
          pt: 0,
        }}
      >
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
}

export default Roles;
